/**
 * Statische Utility-Klasse zum Umgang mit abgeschlossenen Intervallen reeler Zahlen. Ein Interval ist dabei ein Array,
 * [0,1] steht also für das Interval von 0 bis 1.
 */
export default class ClosedInterval {

    /**
     * prüft, ob zwei Intervalle sich schneiden
     * @param {Array} a
     * @param {Array} b
     * @returns {boolean}
     */
    static intersects(a,b) {
        return !(a[1]<b[0] || a[0]>b[1]);
    }

    /**
     * gibt die Vereinigung zweier Intervalle zurück
     * @param {?Array} a
     * @param {?Array} b
     * @returns {?Array}
     */
    static union(a,b) {
        if (a===null || b===null) {
            return a==null ? b : a;
        }
        return [Math.min(a[0],b[0]),Math.max(a[1],b[1])];
    }

    /**
     * gibt die Schnittmenge zweier Intervalle zurück
     * @param {?Array} a
     * @param {?Array} b
     * @returns {?Array}
     */
    static intersection(a,b) {
        if (a===null || b===null) {
            return a==null ? b : a;
        }
        if (ClosedInterval.intersects(a,b)) {
            const l = Math.max(a[0],b[0]);
            const r = Math.min(a[1],b[1]);
            return [l,r];
        }
        return null;
    }

    /**
     * vergrössert ein Interval a um einen festen Betrag x sowohl nach links als auch rechts
     * @param {?Array} a
     * @param {number} x
     * @returns {?Array}
     */
    static expandBy(a,x) {
        if (a===null) {
            return null;
        }
        return [a[0]-x,a[1]+x];
    }

    /**
     * gibt die Länge eines Intervals zurück
     * @param {?Array} a
     */
    static size(a) {
        return a===null ? 0 : a[1]-a[0];
    }

    /**
     * @param intervals
     * @return {Array}
     * @param {...Array<Array>} intervals
     */
    static clone(...intervals) {
        return intervals.map(i=>i.map(r=>r));
    }

    /**
     * sortiert die Grenzen des Intervals, so dass garantiert a[0]<=a[1] ist
     * @param {Array} a
     */
    static order(a) {
        const l = Math.min(a[0],a[1]);
        const r = Math.max(a[0],a[1]);
        a[0] = l;
        a[1] = r;
        return a;

    }

    /**
     * Sortiert eine Liste von Intervallen und stellt sicher, dass bei jedem Intervall das erste Element kleiner
     * als das zweite Element ist, und entfernt Intervalle mit NaN.
     * @param {Array<Array>} intervals
     * @returns {Array<Array>}
     */
    static shapely(intervals) {
        intervals = intervals.filter(i=>!(Number.isNaN(i[0]) || Number.isNaN(i[1])));
        intervals.forEach(i=>{
            if (i[0]>i[1]) {
                const h = i[0];
                i[0] = i[1];
                i[1] = h;
            }
        });
        intervals.sort((a,b)=>a[0]-b[0]);
        return intervals;
    }

    /**
     * Vereinigt alle sich schneidenden Intervalle
     * @param {Array<Array>} intervals
     * @param {number} [epsilon=0.0000001]
     * @returns {Array<Array>}
     */
    static maybeUnion(intervals,epsilon=0.0000001) {
        let last = null;
        const result = [];
        for (let cur of ClosedInterval.shapely(intervals)) {
            if (last==null || last[1]<cur[0]-epsilon) {
                last = [cur[0],cur[1]];
                result.push(last);
            } else {
                if (last[1]<cur[1]+epsilon) {
                    last[1] = cur[1];
                }
            }
        }
        return result;
    }

    /**
     * Entfernt Punkt-Intervalle (kleiner epsilon)
     * @param {Array<Array>} intervals
     * @param {number} [epsilon=0.0000001]
     * @returns {Array<Array>}
     */
    static removePoints(intervals,epsilon=0.0000001) {
        const result = [];
        for (let cur of ClosedInterval.shapely(intervals)) {
            if (cur[1]-cur[0]>epsilon) {
                result.push(cur);
            }
        }
        return result;
    }

    static containsPoint(interval, point) {
        return point >= interval[0] && point <= interval[1];
    }

}
